/**
 * File-picker dedupe — one answer to "did the user already add this file?",
 * shared by every picker in the app (staged import lists, receipt batches,
 * menu uploads, chat attachments).
 *
 * The signature is metadata only — `name|size|lastModified` — the same
 * composite the pickers converged on inline before this module existed.
 * Reading bytes to hash contents would catch a renamed re-pick, but it costs
 * an async read of every file (multi-MB photos included) before the picker
 * can say no, and the realistic duplicate — the same file chosen twice from
 * Recents, or the same folder dropped twice — keeps its name. Meanwhile
 * lastModified + size keep two DIFFERENT files that happen to share a name
 * ("invoice.pdf" from two vendors, a re-exported spreadsheet) from being
 * merged, which is the failure a name-only key would have.
 *
 * Deliberately NOT in the key: file.type (a re-saved export can flip between
 * `text/csv` and `application/vnd.ms-excel` while staying the same pick) and
 * any content bytes (above). Deliberately verbatim: the name — no case or
 * whitespace normalization, because merging "Invoice.pdf" with "invoice.pdf"
 * is exactly the same-name-different-file mistake users warned about.
 *
 * Scope is one staging list, not import history: a picker forgets on Import,
 * so re-importing last week's file on purpose still works.
 *
 * public/js/file-dedupe.js is the UMD mirror for the plain <script> pages
 * (window.FileDedupe). file-dedupe.test.mjs runs one case table through both
 * copies — change one, change the other.
 */
export function fileSignature(file) {
  const f = file || {};
  return `${f.name || ''}|${f.size == null ? 0 : f.size}|${f.lastModified || 0}`;
}

export function signaturesOf(files) {
  return new Set(Array.from(files || []).map(fileSignature));
}

/**
 * Split an incoming pick into files worth staging and duplicates to drop.
 * `existingSignatures` is a Set (or array) of signatures already staged; it
 * is copied, not mutated. A duplicate of an EARLIER file in the same incoming
 * batch is skipped too — a drag-drop DataTransfer can carry the same file
 * twice even though a file dialog cannot.
 */
export function partitionNewFiles(existingSignatures, files) {
  const seen = new Set(existingSignatures || []);
  const added = [];
  const skipped = [];
  for (const file of Array.from(files || [])) {
    const signature = fileSignature(file);
    if (seen.has(signature)) {
      skipped.push(file);
      continue;
    }
    seen.add(signature);
    added.push(file);
  }
  return { added, skipped };
}
